import { sanitizeDeceptioContent } from '@clipr/lib';
import { action, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';

export type StatementSegment = { text: string; highlighted: boolean };

// Splits the analysed statement into plain and highlighted runs. The vendor's `ranges` are
// [start, end) character offsets into the submitted text; they arrive per rule hit, so they can
// overlap and are not guaranteed to be in order. Slicing them as given would emit the statement
// out of sequence, so they are clamped, sorted and merged first.
export function buildStatementSegments(statement: string, ranges: number[][]): StatementSegment[] {
  if (!statement)
    return [];

  const merged = ranges
    .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start)
    // Rounded outward, so a fractional range covers the characters it touches instead of slicing
    // to '' and rendering an empty highlight.
    .map(([start, end]) => [Math.max(0, Math.floor(start)), Math.min(statement.length, Math.ceil(end))])
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0])
    .reduce<number[][]>((acc, range) => {
      const last = acc[acc.length - 1];
      if (last && range[0] <= last[1])
        last[1] = Math.max(last[1], range[1]);
      else
        acc.push([...range]);
      return acc;
    }, []);

  const segments: StatementSegment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor)
      segments.push({ text: statement.slice(cursor, start), highlighted: false });
    segments.push({ text: statement.slice(start, end), highlighted: true });
    cursor = end;
  }
  if (cursor < statement.length)
    segments.push({ text: statement.slice(cursor), highlighted: false });

  return segments;
}

export class DeceptioWindowState
  extends StoreNode {

  readonly nodeType = 'DeceptioWindow';

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    this.window.listen(
      this.windowListener);
  }

  readonly window = new WindowState(this.store);
  @observable isLoading: boolean = false;
  @observable error: string | null = null;
  @observable analysis: DeceptioDetailedAnalysis | null = null;
  @observable statement: string = '';
  // Which rule the explanation and follow-up questions are showing. The vendor returns one rule per
  // hit, so the same keyword can appear twice; the index, not the name, is the identity.
  @observable selectedResultIndex: number | null = null;
  iFrameUrl: string | null = null;

  @action
  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close();
        break;
    }
  }

  @action
  open({ iFrameUrl }: { iFrameUrl: string }) {
    this.dispatch('Overlays', 'openWindow', { name: 'DeceptioWindow' });

    this.iFrameUrl = iFrameUrl;
    this.analysis = null;
    this.statement = '';
    this.selectedResultIndex = null;
    this.error = null;
    this.isLoading = false;
  }

  @action
  async openAnalysis({ jobId, momentId, statement }: {
    jobId: string; momentId: string; statement: string;
  }) {
    this.dispatch('Overlays', 'openWindow', { name: 'DeceptioWindow' });

    this.iFrameUrl = null;
    this.analysis = null;
    // Rule ranges index the string the backend SUBMITTED, so rebuild it with the same shared
    // transform. A plain .trim() here left NULs in place and shifted every later highlight right.
    this.statement = sanitizeDeceptioContent(statement);
    this.selectedResultIndex = null;
    this.error = null;
    this.isLoading = true;

    const [res, err] = await this.store.apiService.retrieveDeceptioAiAnalysis({
      jobId,
      momentId,
    });

    this.isLoading = false;
    if (err) {
      this.error = 'Could not retrieve the Deceptio analysis.';
      return;
    }

    this.analysis = res?.retrieveDeceptioAiAnalysis ?? null;
    this.selectedResultIndex = this.analysis?.results.length ? 0 : null;
  }

  @action
  selectResult(index: number) {
    this.selectedResultIndex = index;
  }

  get selectedResult(): DeceptioRuleResult | null {
    if (this.selectedResultIndex === null)
      return null;
    return this.analysis?.results[this.selectedResultIndex] ?? null;
  }

  get statementSegments(): StatementSegment[] {
    return buildStatementSegments(this.statement, this.selectedResult?.ranges ?? []);
  }

  @action
  close() {
    this.iFrameUrl = null;
    this.analysis = null;
    this.statement = '';
    this.selectedResultIndex = null;
    this.error = null;
    this.isLoading = false;

    this.emit('close');
    this.dispatch('Overlays', 'closeWindow');
  }
}

type DeceptioRuleResult = {
  name: string;
  description: string;
  ranges: number[][];
  followupQuestions: string[];
};

type DeceptioDetailedAnalysis = {
  id: string;
  tokensUsed?: number | null;
  score: number;
  results: DeceptioRuleResult[];
};
